import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';

interface GameRoundProps {
  currentRound: number;
  roundProgress: number;
  restartCount: number;
  hasLost: boolean;
}

const GameRound: React.FC<GameRoundProps> = ({
  currentRound,
  roundProgress,
  restartCount,
  hasLost,
}) => {
  const [barWidth, setBarWidth] = useState(5);
  const barAnimation = useAnimation();
  const roundTextAnimation = useAnimation();

  const MIN_BAR_WIDTH = 5;
  const animationDuration = 0.4;

  // Refs para valores anteriores
  const roundRef = useRef(currentRound);
  const firstRender = useRef(true);
  const progressRef = useRef(roundProgress);
  const lastRestartRef = useRef(restartCount);

  useEffect(() => {
    // Bandera para controlar si el componente está montado
    let isMounted = true;

    // Primer render
    if (firstRender.current) {
      setBarWidth(Math.max(MIN_BAR_WIDTH, roundProgress));
      barAnimation.set({ width: `${Math.max(MIN_BAR_WIDTH, roundProgress)}%` });
      firstRender.current = false;
      return;
    }

    // Si roundProgress es 0 o hay un reinicio, ponemos al mínimo con animación
    if (roundProgress === 0 || restartCount !== lastRestartRef.current) {
      lastRestartRef.current = restartCount;
      if (isMounted) {
        barAnimation.start({
          width: `${MIN_BAR_WIDTH}%`,
          transition: { duration: animationDuration, ease: 'easeInOut' },
        });
        setBarWidth(MIN_BAR_WIDTH);
      }
      return;
    }

    // En cualquier otro caso, actualizamos el progreso
    const newWidth = Math.max(MIN_BAR_WIDTH, roundProgress);
    if (isMounted) {
      barAnimation.start({
        width: `${newWidth}%`,
        transition: { duration: animationDuration, ease: 'easeInOut' },
      });
      setBarWidth(newWidth);
    }

    // Guardamos el progreso actual
    progressRef.current = roundProgress;

    // Limpieza al desmontar
    return () => {
      isMounted = false;
    };
  }, [roundProgress, restartCount, barAnimation, animationDuration]);

  // Efecto separado para animar el texto de la ronda
  useEffect(() => {
    if (firstRender.current) return;

    // Bandera para controlar si el componente está montado
    let isMounted = true;

    const animateText = async () => {
      try {
        if (isMounted) {
          await roundTextAnimation.start({
            scale: 1.15,
            transition: { duration: animationDuration / 2, ease: 'easeOut' },
          });

          // Verificar nuevamente si el componente sigue montado
          if (isMounted) {
            roundTextAnimation.start({
              scale: 1,
              transition: { duration: animationDuration / 2, ease: 'easeIn' },
            });
          }
        }
      } catch (error) {
        // Ignorar errores si el componente se desmontó
        console.log('Animación cancelada porque el componente se desmontó');
      }
    };

    // Iniciar la animación en el siguiente frame para asegurar que el componente esté montado
    const animationFrame = requestAnimationFrame(() => {
      animateText();
    });

    // Limpieza al desmontar
    return () => {
      isMounted = false;
      cancelAnimationFrame(animationFrame);
    };
  }, [currentRound, roundTextAnimation, animationDuration]);

  return (
    <div className="bg-gray-300 dark:bg-gray-600 rounded-md flex items-center mx-2 py-1 px-2 relative overflow-hidden">
      {/* Contenedor fondo de la barra */}
      <div className="absolute inset-0 z-0 rounded-md overflow-hidden">
        <motion.div
          className="h-full bg-yellow-500"
          style={{ width: `${barWidth}%` }}
          animate={barAnimation}
        />
      </div>

      {/* Texto "Ronda X" */}
      <motion.div
        className="flex items-center relative z-20"
        animate={roundTextAnimation}
      >
        <span
          style={{ marginRight: currentRound < 10 ? '-0.5rem' : '0' }}
          className="font-semibold text-xl"
        >
          Ronda
        </span>
        <span className="text-xl text-end inline-block min-w-[30px]">
          {currentRound}
        </span>
      </motion.div>
    </div>
  );
};

export default GameRound;
